import memoize from 'memoize-one';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { logQuestionAnswerKey } from 'my-core/get-quiz-question-answer-key';
import { useCurrentUser, useSearchParams, useStandardApiRequest } from 'my-core/hooks';

import PracticeQuestion from './PracticeQuestion';

import { createQuestionSubmission, resetQuizQuestionSubmission } from 'my-actions/QuizQuestionSubmissionActions';

const TOKEN_PRICE = 1;
export default function PracticeQuestionCtnr({
  attemptContext,
  canView,
  createStatus,
  onlineCourse,
  onlineCourseId,
  quizQuestion,
  resetStatus,
  tokenBalance,
  ...rest
}) {
  const ocId = onlineCourse?.id || onlineCourseId || null; // onlineCourseId only if course-assignment, null if CARS Passage
  const [searchParams] = useSearchParams();
  const defaultSubQuestionId = searchParams.subQId;
  // A notification link names its question and reply too, so a second one to the part already linked still opens it.
  const subQuestionLinkKey =
    defaultSubQuestionId && [defaultSubQuestionId, searchParams.questionId, searchParams.replyId].join();

  const currentUser = useCurrentUser();
  const submission = useSelector(state =>
    getSubmission(
      state.quizQuestionSubmissions.items,
      state.onlineCourses.items,
      ocId,
      quizQuestion?.id,
      currentUser.id,
    ),
  );

  const { performRequest: performCreateSubmission } = useStandardApiRequest({
    actionCreator: createQuestionSubmission,
  });

  // Resolves once the answer is on the server. Callers chain `onCompleted` off this, so whoever
  // is told the activity is finished can read the result back and find it there.
  const saveSubmission = useCallback(
    (submission, params) => {
      if (currentUser.guest) return Promise.resolve();
      return performCreateSubmission({
        online_course_id: ocId,
        quiz_question_id: quizQuestion.id,
        submission,
        ...attemptContext,
        ...params,
      });
    },
    [currentUser, performCreateSubmission, ocId, quizQuestion?.id, attemptContext],
  );

  const { performRequest: performResetSubmission } = useStandardApiRequest({
    actionCreator: resetQuizQuestionSubmission,
  });

  const resetSubmission = useCallback(() => {
    performResetSubmission(submission.id);
  }, [performResetSubmission, submission?.id]);

  const canTokenPurchase = tokenBalance >= TOKEN_PRICE;

  if (__DEV__) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!quizQuestion?.id) return;
      logQuestionAnswerKey(quizQuestion);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizQuestion?.id]);
  }

  return (
    <PracticeQuestion
      {...rest}
      autoSave
      canCheckSolution={canView}
      canTokenPurchase={canTokenPurchase}
      defaultSubQuestionId={defaultSubQuestionId}
      onlineCourse={onlineCourse}
      onlineCourseId={ocId}
      onResetSubmission={resetSubmission}
      onSaveSubmission={saveSubmission}
      quizQuestion={quizQuestion}
      submission={submission}
      subQuestionLinkKey={subQuestionLinkKey}
    />
  );
}

const getSubmission = memoize((submissions, onlineCourses, ocId, qqId, uId) => {
  const includeNullOcId = onlineCourses[ocId]?.course_type === 'practice_bank';
  return Object.values(submissions).find(
    s =>
      s.user_id === uId &&
      s.quiz_question_id === qqId &&
      (ocId === null || s.online_course_id === ocId || (includeNullOcId && s.online_course_id === null)) &&
      !s.quiz_id,
  );
});
